import React, {useMemo} from 'react';
import {View, TouchableOpacity, StyleSheet} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {RootNavigatorParams} from 'navigation/Routes';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Icon, Text} from 'ui-kits';
interface BreadcrumbItem {
  key: string;
  name: string;
  isActive: boolean;
}

interface BreadcrumbsProps {
  projectName: string;
}

const Breadcrumbs = ({projectName}: BreadcrumbsProps) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootNavigatorParams>>();

  const breadcrumbTrail = useMemo(() => {
    const state = navigation.getState();
    if (!state) {
      return [];
    }

    const {routes, index} = state;

    // Get the last navigated route
    const lastRoute = routes[index];

    // Create the breadcrumb trail with project name and last navigated route
    const trail = [
      {
        key: 'projectName',
        name: projectName,
        isActive: false,
      },
      {
        key: lastRoute.key,
        name: lastRoute.name,
        isActive: true,
      },
    ];
    return trail;
  }, [navigation, projectName]);

  const handleNavigate = (item: BreadcrumbItem) => {
    if (item.key === 'projectName') {
      navigation.navigate('Project Status' as never);
      return;
    }

    const state = navigation.getState();
    if (!state) {
      return;
    }

    const {routes, index} = state;
    const targetIndex = routes.findIndex(r => r.name === item.name);

    if (targetIndex !== -1 && targetIndex < index) {
      const popCount = index - targetIndex;
      navigation.pop(popCount);
    } else {
      navigation.navigate(item.name as never);
    }
  };

  return (
    <View style={styles.container}>
      {breadcrumbTrail.map((item, idx) => (
        <BreadcrumbItemComponent
          key={item.key}
          item={item}
          isLast={idx === breadcrumbTrail.length - 1}
          onNavigate={handleNavigate}
        />
      ))}
    </View>
  );
};

const BreadcrumbItemComponent = ({
  item,
  isLast,
  onNavigate,
}: {
  item: BreadcrumbItem;
  isLast: boolean;
  onNavigate: (item: BreadcrumbItem) => void;
}) => (
  <View style={styles.breadCrumbTrail}>
    <View
      style={[
        styles.breadcrumbItem,
        item.isActive && styles.activeBreadCrumb,
      ]}>
      <TouchableOpacity onPress={() => onNavigate(item)}>
        <Text style={[styles.text, item.isActive && styles.active]}>
          {item.name}
        </Text>
      </TouchableOpacity>
    </View>
    {!isLast && <Icon type="cheveron_right_grey" />}
  </View>
);

export default Breadcrumbs;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing['2'],
  },
  breadCrumbTrail: {
    flexDirection: 'row',
    gap: spacing['2'],
    alignItems: 'center',
  },
  breadcrumbItem: {
    borderWidth: 1.5,
    borderColor: colors.palette.stroke,
    flexDirection: 'row',
    alignItems: 'center',
    color: colors.palette.secondaryBlack5,
    paddingVertical: spacing['1'],
    paddingHorizontal: spacing['2'],
    borderRadius: corner_radius.rounded,
  },
  activeBreadCrumb: {
    backgroundColor: colors.palette.brandBlue,
    border: 'none',
  },
  text: {
    color: '#555',
    fontSize: 16,
  },
  active: {
    color: colors.palette.white,
    fontWeight: 600,
  },
});
